import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "../components/Nav";
import SEO, { buildBreadcrumb } from "../components/SEO";
import ArtistListingCard from "../components/ArtistListingCard";
import ArtistRecommendations from "../components/ArtistRecommendations";
import api from "../lib/api";

/**
 * /artists/city/:slug — SEO-optimised city landing page.
 */
export default function CityLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/seo/city/${slug}`).then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
  }, [slug]);

  if (notFound) {
    return (
      <div>
        <SEO title="City not found" noindex path={`/artists/city/${slug}`} />
        <Nav />
        <section className="section container city-landing-not-found" data-testid="city-not-found">
          <div className="city-landing-empty-icon" aria-hidden="true">⌖</div>
          <p className="city-landing-eyebrow">Explore local talent</p>
          <h1>We couldn’t find that city</h1>
          <p className="city-landing-not-found-copy">Try searching for a city or browse all available artists.</p>
          <Link to="/search" className="btn btn-gold" data-testid="city-back-search">Browse all artists →</Link>
        </section>
</div>
    );
  }

  const city = data?.city;
  const artists = data?.artists || [];
  const title = city ? `Book verified artists in ${city.name}` : "Loading…";
  const description = city
    ? `${data?.total || 0} verified artists available in ${city.name} — singers, DJs, comedians, dancers, anchors and more. Book for weddings, corporate events, birthdays and private parties.`
    : "";

  const itemListLd = {
    "@context": "https://schema.org", "@type": "ItemList",
    itemListElement: artists.slice(0, 20).map((a, i) => ({
      "@type": "ListItem", position: i + 1,
      url: `https://booktalent.in/artist/${a.slug || a.user_id}`, name: a.stage_name,
    })),
  };

  return (
    <div data-testid={`city-landing-${slug}`}>
      <SEO
        title={city ? `Book Artists in ${city.name}` : "City"}
        description={description}
        keywords={city ? `artists in ${city.name}, book singer in ${city.name}, DJ ${city.name}, event artists ${city.name}` : ""}
        path={`/artists/city/${slug}`}
        jsonLd={city ? [itemListLd, buildBreadcrumb([
          { name: "Home", url: "/" },
          { name: "Cities", url: "/search" },
          { name: city.name, url: `/artists/city/${slug}` },
        ])] : null}
      />
      <Nav />
      <section className="section container city-landing-section">
        {!data && (
          <div className="city-landing-skeleton" aria-label="Loading city artists">
            <div className="skeleton" />
          </div>
        )}
        {data && city && (
          <>
            <header className="city-landing-intro" data-testid="city-hero">
              <nav className="city-landing-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span aria-hidden="true">/</span>
                <Link to="/search">Artists</Link>
                <span aria-hidden="true">/</span>
                <span aria-current="page">{city.name}</span>
              </nav>
              <div className="city-landing-intro-main">
                <div>
                  <div className="city-landing-eyebrow">Discover local talent</div>
                  <h1 data-testid="city-h1">{city.hero_title || title}</h1>
                  <p data-testid="city-hero-sub">{city.hero_subtitle || description}</p>
                </div>
                <div className="city-landing-intro-action">
                  <span className="city-landing-count">
                    {Number(data.total || 0).toLocaleString("en-IN")} verified {Number(data.total) === 1 ? "artist" : "artists"}
                  </span>
                  <Link to="/search" className="btn btn-gold" data-testid="city-hero-cta">
                    Browse artists
                  </Link>
                </div>
              </div>
            </header>
            <div className="city-landing-results-heading">
              <div>
                <h2>Artists in {city.name}</h2>
                <p>Compare profiles, ratings and starting prices to find the right fit for your event.</p>
              </div>
              <span>{Number(data.total || 0).toLocaleString("en-IN")} available</span>
            </div>
            {artists.length === 0 ? (
              <>
                <div className="city-landing-empty" data-testid="city-empty-state">
                  <span className="city-landing-empty-icon" aria-hidden="true">✦</span>
                  <h3>No artists in {city.name} yet</h3>
                  <p>We’re growing our local artist community. In the meantime, browse artists from other cities.</p>
                  <Link to="/search" className="btn btn-ghost">Browse all artists →</Link>
                </div>
                <ArtistRecommendations title="You may also like" testId="city-artist-recommendations" />
              </>
            ) : (
              <div className="artist-grid-v2">
                {artists.map((a) => (
                  <ArtistListingCard key={a.user_id} artist={a} testId={`city-card-${a.user_id}`} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
</div>
  );
}
